import { useState } from 'react';
import { Check, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import cx from 'classnames';
import { ITestDefinition } from '../../models/testDefinition';
import { IEnabledTest } from '../../models/teamTestConfig';
import styles from './TestConfigItem.module.scss';

interface TestConfigItemProps {
  test: ITestDefinition;
  enabled: boolean;
  enabledTest: IEnabledTest | undefined;
  onToggle: () => void;
  onToggleMetric: (metric: string) => void;
  onUnitLabelChange: (unit: string, label: string) => void;
}

export default function TestConfigItem({
  test,
  enabled,
  enabledTest,
  onToggle,
  onToggleMetric,
  onUnitLabelChange,
}: TestConfigItemProps) {
  const [labelsOpen, setLabelsOpen] = useState(false);

  const activeMetrics = enabledTest?.metrics ?? test.outputMetrics ?? [];
  const isMultiMetric = (test.outputMetrics?.length ?? 0) > 1;
  const isMultiUnit = (test.allowedUnits?.length ?? 0) > 1;

  // Unità abilitate su cui mostrare il pannello label
  const unitsList: string[] = test.allowedUnits ?? (test.defaultUnit ? [test.defaultUnit] : []);

  return (
    <div className={styles.wrapper}>
      {/* ─── Riga principale (checkbox + nome + unità) ─── */}
      <label className={cx(styles.testItem, enabled && styles.testItemEnabled)}>
        <input
          type="checkbox"
          checked={enabled}
          onChange={onToggle}
          className={styles.hiddenInput}
        />
        <div className={cx(styles.checkbox, enabled && styles.checkboxChecked)}>
          {enabled && <Check size={12} strokeWidth={3} />}
        </div>
        <div className={styles.testInfo}>
          <span className={styles.testName}>{test.name}</span>
          <span className={styles.testUnit}>
            {isMultiUnit ? test.allowedUnits!.join(' · ') : test.defaultUnit}
          </span>
        </div>
      </label>

      {/* ─── Chip metriche multi-metrica (sez.2.2) ─── */}
      {enabled && isMultiMetric && (
        <div className={styles.metricChips}>
          {test.outputMetrics!.map((m) => {
            const on = activeMetrics.includes(m);
            return (
              <button
                key={m}
                type="button"
                onClick={() => onToggleMetric(m)}
                className={cx(styles.metricChip, on && styles.metricChipOn)}
              >
                {m}
              </button>
            );
          })}
        </div>
      )}

      {/* ─── Pannello label personalizzate (solo multi-unità abilitato) ─── */}
      {enabled && isMultiUnit && (
        <div className={styles.labelPanel}>
          {/* Toggle collassa/espandi */}
          <button
            type="button"
            className={styles.labelToggle}
            onClick={() => setLabelsOpen((v) => !v)}
          >
            <span>Personalizza etichette</span>
            {labelsOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>

          {labelsOpen && (
            <div className={styles.labelPanelContent}>
              {/* Warning multi-unit analytics — bug preesistente documentato */}
              <div className={styles.labelWarning}>
                <AlertTriangle size={12} />
                <span>
                  Con più unità abilitate, gli analytics aggregano senza filtrare per unità (limite noto).
                </span>
              </div>

              {/* Input label per ogni unità disponibile */}
              {unitsList.map((unit) => (
                <div key={unit} className={styles.labelRow}>
                  <span className={styles.unitChip}>{unit}</span>
                  <span className={styles.labelArrow}>→</span>
                  <input
                    type="text"
                    maxLength={30}
                    placeholder="es. Carico gara"
                    value={enabledTest?.unitLabels?.[unit] ?? ''}
                    onChange={(e) => onUnitLabelChange(unit, e.target.value)}
                    className={styles.labelInput}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
